import { GrCalendar, GrHome, GrMenu } from 'react-icons/gr';
import { MdLogout } from 'react-icons/md';

import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Outlet, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import ThemeIcon from './components/ThemeIcon';
import { HiUser } from 'react-icons/hi';

import ConfirmationProvider from './contexts/ConfirmationProvider';
import FilterClickCheckProvider from './contexts/FilterClickCheckProvider';

import { useOktaAuth } from '@okta/okta-react';
import LoadingProvider from './contexts/LoadingProvider';
import { ThemeContext } from './contexts/ThemeContext';
import { UsersnapProvider } from './contexts/UsersnapContext';
import { MyRoleContext, AdminProvider } from './contexts/AdminContext';
import usePageTitle from './hooks/usePageTitle';
import { AuthService } from './services/AuthService';
import { DrawerPreference } from './types/Preference';

const queryClient = new QueryClient();
export type NavItem = {
  title: string;
  icon: React.ReactElement;
  route: string;
  children?: NavItem[];
  hideInProd?: boolean;
  shortTitle?: string;
};

export const navigationItems: NavItem[] = [
  { title: 'Home', icon: <GrHome size={24} />, route: '/home' },
  {
    title: 'Admin',
    icon: <GrHome size={24} />,
    route: '',
    children: [{ title: 'USER MANAGEMENT', icon: <GrCalendar size={24} />, route: '/userManagement' }],
  },
  {
    title: 'Lowes',
    icon: <GrHome size={24} />,
    route: '',
    children: [{ title: 'RECORDS', icon: <GrCalendar size={24} />, route: '/records' }],
  },
];

const App = () => {
  const navigate = useNavigate();
  const themes = ['dark', 'light', 'imagine' /*, 'cupcake'*/];
  const [theme, setTheme] = useState(themes[2]);
  const [env, setEnv] = useState<string>();
  const [showUsersnap, setShowUsersnap] = useState<boolean>(false);
  const { role } = useContext(MyRoleContext);
  const [drawerConfig, setDrawerConfig] = useState<DrawerPreference[]>(
    navigationItems.map((n) => {
      return { name: n.title, isOpen: true };
    }),
  );

  const { oktaAuth, authState } = useOktaAuth();
  usePageTitle();

  useEffect(() => {
    const storedPreference = localStorage.getItem('preferredTheme');
    const storedDrawerPreference = localStorage.getItem('drawerPreference');
    if (storedPreference) {
      setTheme(storedPreference);
    }

    if (storedDrawerPreference) {
      const prefObj: DrawerPreference[] = JSON.parse(storedDrawerPreference);
      setDrawerConfig(prefObj);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('preferredTheme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('drawerPreference', JSON.stringify(drawerConfig));
  }, [drawerConfig]);

  const handleLogout = async () => {
    await AuthService.logout();
  };

  const onToggleDrawer = (e: ChangeEvent<HTMLInputElement>) => {
    if (drawerConfig.findIndex((x) => x.name === e.target.name) < 0) {
      const newPreference: DrawerPreference[] = [...drawerConfig, { name: e.target.name, isOpen: false }];
      setDrawerConfig(newPreference);
    } else {
      const newPreference: DrawerPreference[] = drawerConfig.map((d) => {
        if (d.name === e.target.name) {
          return { name: d.name, isOpen: !d.isOpen };
        }
        return d;
      });
      setDrawerConfig(newPreference);
    }
  };
  const navigateToPage = (route: string) => {
    document.getElementById('my-drawer-3')?.click();
    navigate(route);
  };

  const userIsAdmin = role?.isAdmin === true;

  const navigationElements = navigationItems
    .filter((i) => (!i.hideInProd || env !== 'prod') && (i.title !== 'Admin' || userIsAdmin))
    .map((n) => {
      return n.children ? (
        <div key={n.title} className="collapse collapse-arrow bg-base-100 my-1">
          <input
            type="checkbox"
            name={n.title}
            checked={drawerConfig.find((d) => d.name === n.title)?.isOpen ?? true}
            onChange={(e) => onToggleDrawer(e)}
          />
          <div className="collapse-title text-lg flex flex-row justify-between">
            <div>{n.title}</div>
          </div>
          <div className="collapse-content">
            {n.children
              ?.filter((i) => (!i.hideInProd || env !== 'prod') && (i.title !== 'Admin' || userIsAdmin))
              .map((c) => {
                return (
                  <div key={c.route}>
                    <li>
                      <div
                        className="flex flex-row align-center justify-between"
                        onClick={() => navigateToPage(c.route)}>
                        <div className="text-lg">{c.title}</div>
                        <div>{c.icon}</div>
                      </div>
                    </li>
                  </div>
                );
              })}
          </div>
          <div className="divider my-0"></div>
        </div>
      ) : (
        <div key={n.route}>
          <li>
            <div className="flex flex-row align-center justify-between" onClick={() => navigateToPage(n.route)}>
              <div className="text-lg">{n.title}</div>
              <div>{n.icon}</div>
            </div>
          </li>
          <div className="divider my-0"></div>
        </div>
      );
    });

  const toggleTheme = () => {
    const currentIndex = Math.max(0, themes.indexOf(theme));
    const nextTheme = themes[(currentIndex + 1) % themes.length];

    setTheme(nextTheme);
  };
  return (
    <QueryClientProvider client={queryClient}>
      <AdminProvider>
        <ThemeContext.Provider value={theme}>
          <UsersnapProvider
            initParams={{ user: { userId: showUsersnap ? '1' : '' }, nativeScreenshot: { target: '_blank' } }}>
            <div className="drawer" data-theme={theme}>
              <input id="my-drawer-3" type="checkbox" className="drawer-toggle" />
              <div className="drawer-content flex flex-col bg-base-300 h-screen">
                {/* Navbar */}

                <div className="w-full min-w-full navbar flex flex-row bg-base-100 justify-between">
                  <div className="flex flex-row">
                    <div>
                      {!authState || !authState.isAuthenticated ? null : (
                        <label htmlFor="my-drawer-3" aria-label="open sidebar" className="btn btn-square btn-ghost">
                          <GrMenu size={25} />
                        </label>
                      )}
                    </div>
                    <div
                      className={`px-2 mx-2 font-bold text-2xl ${
                        authState?.isAuthenticated ? 'hover:text-primary hover:cursor-pointer' : ''
                      }`}
                      onClick={() => authState?.isAuthenticated && navigate('/home')}>
                      Lowes Dashboard
                    </div>
                  </div>

                  <div className="flex flex-row items-center gap-4">
                    {authState?.idToken?.claims.name && (
                      <div className="badge badge-lg badge-primary gap-2">
                        <HiUser className="text-sm" />
                        <span className="font-semibold text-sm">{authState?.idToken?.claims.name}</span>
                      </div>
                    )}
                    <div className="flex flex-row gap-2">
                      {!authState || !authState.isAuthenticated ? null : (
                        <button onClick={handleLogout} className="btn btn-ghost flex items-center gap-2">
                          <MdLogout /> Logout
                        </button>
                      )}
                      <label className="swap swap-rotate">
                        {/* this hidden checkbox controls the state */}
                        <input type="checkbox" className="theme-controller" value={theme} onChange={toggleTheme} />
                        <ThemeIcon theme={theme} />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Page content here */}
                <div className="flex-1 flex flex-col sm:m-3 overflow-hidden">
                  <FilterClickCheckProvider>
                    <LoadingProvider>
                      <ConfirmationProvider>
                        <Outlet />
                      </ConfirmationProvider>
                    </LoadingProvider>
                  </FilterClickCheckProvider>
                </div>
              </div>

              {!authState || !authState.isAuthenticated ? null : (
                <div className="drawer-side shadow-xl z-50">
                  <label htmlFor="my-drawer-3" aria-label="close sidebar" className="drawer-overlay"></label>
                  <ul className="menu p-4 w-80 min-h-full bg-base-100 justify-between">
                    <ul>{navigationElements}</ul>
                    <button onClick={handleLogout} className="btn btn-sm w-full btn-primary flex items-center gap-2">
                      <MdLogout /> Logout
                    </button>
                  </ul>
                </div>
              )}
            </div>
            <ToastContainer
              toastStyle={{
                backgroundColor: theme === 'dark' ? '#22252a' : 'default',
                color: theme === 'dark' ? '#ccc' : 'default',
              }}
            />
          </UsersnapProvider>
        </ThemeContext.Provider>{' '}
      </AdminProvider>{' '}
    </QueryClientProvider>
  );
};

export default App;
